import { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import { Strings } from "@app/constants";
import { FormProvider, StepperHeader } from "@app/components";
import { headersSteps } from "../stepperLabel";
import { Eligibility } from "./eligibility";
import {
  Brand,
  CarInfo,
  Images,
  KmDriven,
  MobileInfo,
  Model,
  SellingTime,
  Variant,
  Year,
} from "../steps";
import { CarValuation } from "./carValuation";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { sellCar } from "../slice";
import { useLocation, useNavigate } from "react-router-dom";
import { useRequestState } from "@app/hooks";
import { selectSellCarState } from "../selector";
import { PATH_DASHBOARD } from "@app/routes/paths";
import { WarningBox } from "./warning";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { selectMyProfileState } from "@app/modules/my-profile/selector";

const SellCar = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [getCarValue, setGetCarValue] = useState(false);
  const scrollBoxRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const [openWarning, setOpenWarning] = useState(false);
  const [warningShownOnce, setWarningShownOnce] = useState(true);

  const { formValue } = location.state || [];
  const schema = Yup.object().shape({
    carNumber: Yup.string().required("Car number is required").trim(),
    brandId: Yup.string().required("Please select a brand"),
    modelId: Yup.string().required("Please select a car model"),
    year: Yup.string().required("Please select manufacturing year"),
    engineType: Yup.string().required("Please select engine specification"),
    transmission: Yup.string().required("Please select transmission type"),
    variant: Yup.string().required("Please select a car variant"),
    color: Yup.string().required("Please enter car color").trim(),
    KmDriven: Yup.string().required("Please select KM driven"),
    selectedSellingTime: Yup.string().required("Please select selling time"),
    carInfo: Yup.object().shape({
      carCondition: Yup.string().required("Please select car condition"),
      insurance: Yup.object().shape({
        isInsuranceAvailable: Yup.string().required("Please select if insurance is available"),
        insuranceType: Yup.string().when("isInsuranceAvailable", {
          is: (val: string) => val === "Yes",
          then: (s) => s.required("Please select insurance type"),
          otherwise: (s) => s.notRequired(),
        }),
        anyClaimsMade: Yup.string().when("isInsuranceAvailable", {
          is: (val: string) => val === "Yes",
          then: (s) => s.required("Please select if any claims were made"),
          otherwise: (s) => s.notRequired(),
        }),
      }),
      Ownership: Yup.object().shape({
        usageType: Yup.string().required("Please select usage type"),
        rcAvailable: Yup.string().required("Please select if RC is available"),
        isLoan: Yup.string().required("Please select loan status"),
      }),
    }),
    image: Yup.object().shape({
      frontView: Yup.mixed().required("Front view image is required"),
    }),
    mobileInfo: Yup.object().shape({
      phoneNumber: Yup.string()
        .required("Mobile number is required")
        .matches(
          /^(\+91)?[6-9]\d{9}$/,
          "Enter a valid 10-digit mobile number"
        ),
      name: Yup.string()
        .required("Name is required")
        .min(2, "Name must be at least 2 characters"),
      emailAddress: Yup.string()
        .required("Email is required")
        .email("Enter a valid email address"),
    }),
  });
  const stepFields = [
    ["carNumber", "brandId"],
    ["modelId"],
    ["year"],
    ["engineType", "transmission", "variant", "color"],
    ["KmDriven"],
    ["selectedSellingTime"],
    [
      "carInfo.carCondition",
      "carInfo.insurance.isInsuranceAvailable",
      "carInfo.insurance.insuranceType",
      "carInfo.insurance.anyClaimsMade",
      "carInfo.Ownership.usageType",
      "carInfo.Ownership.rcAvailable",
      "carInfo.Ownership.isLoan",
    ],
    ["image.frontView"],
    ["mobileInfo.name", "mobileInfo.emailAddress", "mobileInfo.phoneNumber"],
  ] as const;

  const methods = useForm<any>({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      carNumber: "",
      brandId: "",
      modelId: "",
      year: "",
      engineType: "",
      transmission: "",
      variant: "",
      color: "",
      KmDriven: "",
      selectedSellingTime: "",
      carInfo: {
        carCondition: "",
        insurance: {
          isInsuranceAvailable: "",
          insuranceType: "",
          expiryDate: null,
          anyClaimsMade: "",
        },
        Ownership: {
          usageType: "",
          rcAvailable: "",
          isLoan: "",
        },
        accidentAndService: {
          accidentHistory: "",
          fullServiceRecords: "",
        },
      },
      image: {
        frontView: null,
        backView: null,
        leftSideView: null,
        rightSideView: null,
        interiorDashboard: null,
        odometerReading: null,
      },
      mobileInfo: {
        phoneNumber: "",
        name: "",
        emailAddress: "",
        state: "",
        city: "",
        zipCode: "",
      },
    },
  });
  const formWatchedValues = methods.watch();


  console.log("ProfileState_________", formWatchedValues)

  const scrollToTop = () => {
    scrollBoxRef.current?.scrollIntoView();
  };


  console.log("Error______", methods.formState.errors);

  useEffect(() => {
    scrollToTop();
  }, [activeStep]);

  useEffect(() => {
    window.history.pushState({ page: "sellCar" }, "", window.location.href);

    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();
      if (warningShownOnce) {
        setOpenWarning(true);
      } else {
        window.removeEventListener("popstate", handlePopState);
        window.history.back();
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [warningShownOnce]);

  const handleOnSuccess = useCallback(() => {
    navigate(PATH_DASHBOARD.home);
  }, []);

  useRequestState({
    stateSelector: selectSellCarState,
    successMessageShown: true,
    errorShown: true,
    onSuccess: handleOnSuccess,
  });

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
      setGetCarValue(false);
    }
  };

  const handleNext = async () => {
    const isValid = await methods.trigger(stepFields[activeStep] as any);

    if (!isValid) return;

    if (activeStep < headersSteps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleStepClick = async (stepIndex: number) => {
    if (stepIndex < activeStep) {
      setActiveStep(stepIndex);
      return;
    }
    const isValid = await methods.trigger(stepFields[activeStep] as any);
    if (!isValid) return;

    for (let i = activeStep; i < stepIndex; i++) {
      const stepValid = await methods.trigger(stepFields[i] as any);
      if (!stepValid) {
        setActiveStep(i);
        return;
      }
    }
    setActiveStep(stepIndex);
  };
  const convertToBase64 = (file: any) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const handleSubmit = async (formData: any) => {
    console.log("FORM DATA →", formData);
    const files = [
      formData?.image?.backView,
      formData?.image?.frontView,
      formData?.image?.leftSideView,
      formData?.image?.rightSideView,
      formData?.image?.interiorDashboard,
      formData?.image?.odometerReading,
    ];

    const fd = new FormData();

    files.forEach((file) => {
      if (file) fd.append("images", file);
    });

    fd.append("carNumber", formData?.carNumber || "");
    fd.append("brandId", formData?.brandId || "");
    fd.append("modelId", formData?.modelId || "");
    fd.append("engineType", formData?.engineType || "");
    fd.append("transmission", formData?.transmission || "");
    fd.append("manufacturingYear", formData?.year || "");
    fd.append("driven", formData?.KmDriven || "");
    fd.append("variant", String(formData?.variant || ""));
    fd.append("sellingTime", formData?.selectedSellingTime || "");
    fd.append("color", formData?.color || "");

    // Insurance
    fd.append(
      "insuranceAvailable",
      (formData?.carInfo?.insurance?.isInsuranceAvailable === "Yes").toString(),
    );
    fd.append(
      "insuranceType",
      formData?.carInfo?.insurance?.insuranceType || "",
    );
    fd.append(
      "anyClaimsMade",
      (formData?.carInfo?.insurance?.anyClaimsMade === "Yes").toString(),
    );

    // Ownership
    fd.append("usageType", formData?.carInfo?.Ownership?.usageType || "");
    fd.append(
      "rcAvailable",
      (formData?.carInfo?.Ownership?.rcAvailable === "Yes").toString(),
    );
    fd.append(
      "loanHypothecation",
      (formData?.carInfo?.Ownership?.isLoan === "Yes").toString(),
    );

    // Accident & Service
    fd.append(
      "accidentHistory",
      formData?.carInfo?.accidentAndService?.accidentHistory || "",
    );
    fd.append(
      "serviceHistory",
      (formData?.carInfo?.accidentAndService?.fullServiceRecords === "Yes").toString(),
    );

    // Car Condition
    fd.append("carCondition", formData?.carInfo?.carCondition || "");

    // User & Location Info
    fd.append("name", formData?.mobileInfo?.name || "");
    fd.append("email", formData?.mobileInfo?.emailAddress || "");
    fd.append("phone", formData?.mobileInfo?.phoneNumber || "");
    fd.append("phoneNumber", formData?.mobileInfo?.phoneNumber || "");
    fd.append("city", formData?.mobileInfo?.city || "");
    fd.append("state", formData?.mobileInfo?.state || "");
    fd.append("zipCode", formData?.mobileInfo?.zipCode || "");

    // Optional Date
    if (formData?.carInfo?.insurance?.expiryDate) {
      fd.append(
        "insuranceValidTill",
        new Date(formData.carInfo.insurance.expiryDate)
          .toISOString()
          .split("T")[0],
      );
    }

    // Final Dispatch
    dispatch(sellCar(fd));

    console.log("FORMDATA SENT →", Object.fromEntries(fd));
  };

  return (
    <>
      <FormProvider methods={methods}>
        {getCarValue ? (
          <Stack alignItems={"center"} justifyContent={"center"}>
            <Stack
              ref={scrollBoxRef}
              sx={{
                width: { xs: "96%", md: "80%" },
                alignSelf: "center",
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 1,
                px: 2,
                py: 5,
                my: 5,
              }}
            >
              <CarValuation
                onBack={handleBack}
                methods={methods}
                handleSubmit={handleSubmit}
                formWatchedValues={formWatchedValues}
              />
            </Stack>
          </Stack>
        ) : (
          <Stack ref={scrollBoxRef}>
            <StepperHeader
              headersSteps={headersSteps}
              activeStep={activeStep}
              onStepClick={handleStepClick}
            />

            <Stack
              sx={{
                px: { xs: 1, md: 2 },
                py: { xs: 1, md: 2 },
                alignItems: "center",
              }}
            >
              <Eligibility />
            </Stack>

            <Stack
              sx={{
                width: { xs: "96%", md: "80%" },
                alignSelf: "center",
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 1,
                p: 2,
              }}
            >
              {activeStep === 0 && (
                <Brand methods={methods} formValue={formValue} />
              )}
              {activeStep === 1 && <Model methods={methods} />}
              {activeStep === 2 && <Year methods={methods} />}
              {activeStep === 3 && <Variant methods={methods} />}
              {activeStep === 4 && <KmDriven methods={methods} />}
              {activeStep === 5 && <SellingTime methods={methods} />}
              {activeStep === 6 && <CarInfo methods={methods} />}
              {activeStep === 7 && <Images methods={methods} />}
              {activeStep === 8 && (
                <MobileInfo
                  methods={methods}
                  setGetCarValue={setGetCarValue}
                  setActiveStep={setActiveStep}
                  handleSubmit={handleSubmit}
                />
              )}
            </Stack>
          </Stack>
        )}

        {activeStep <= 8 && (
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            px="10%"
            mt={4}
            mb={2}
          >
            <Button
              variant="contained"
              onClick={handleBack}
              disabled={activeStep === 0}
            >
              {Strings.button.back}
            </Button>

            <Button
              variant="contained"
              onClick={handleNext}
              disabled={activeStep === headersSteps.length - 1}
            >
              {Strings.button.next}
            </Button>
          </Stack>
        )}
      </FormProvider>
      <WarningBox
        openWarning={openWarning}
        setOpenWarning={setOpenWarning}
        setWarningShownOnce={setWarningShownOnce}
      />
    </>
  );
};

export { SellCar };
